import { forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import Skeleton from "@mui/material/Skeleton";
import {
  useGridApiContext,
  useGridSelector,
  gridDimensionsSelector,
  gridVisibleColumnDefinitionsSelector,
} from "@mui/x-data-grid-pro";

// Minimum skeleton rows to always render while loading. The native skeleton
// overlay derives its row count from the *previous* result's height, so it
// shows nothing on the first search and only a couple of rows when the prior
// result was small. We keep a floor here so the loading state always reads as
// a populated table.
const MIN_SKELETON_ROWS = 5;

// Varied bar widths so the skeleton looks organic rather than a rigid grid.
const CELL_WIDTHS = ["62%", "80%", "45%", "70%", "55%", "88%"];

/**
 * Custom DataGrid loading overlay: renders column-aligned skeleton rows and
 * never drops below MIN_SKELETON_ROWS, while still filling the viewport when
 * it is taller (e.g. re-searching over a full page).
 *
 * Kept in the in-flow "skeleton" wrapper mode by passing
 * slotProps.loadingOverlay = { variant: "skeleton", noRowsVariant: "skeleton" }
 * in MuiTable, so this content gets a real height to live in.
 */
const LoadingSkeletonOverlay = forwardRef(function LoadingSkeletonOverlay(
  { minRows = MIN_SKELETON_ROWS },
  ref
) {
  const apiRef = useGridApiContext();
  const dimensions = useGridSelector(apiRef, gridDimensionsSelector);
  const columns = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector);

  const rowHeight = dimensions?.rowHeight || 52;

  // Always exactly `minRows` fixed-height skeleton rows, regardless of the
  // previous result size or the overlay box height.
  const rows = useMemo(() => Array.from({ length: minRows }), [minRows]);

  return (
    <div ref={ref} className="rg-skeleton" style={{ height: minRows * rowHeight }}>
      {rows.map((_, r) => (
        <div className="rg-skeleton__row" style={{ height: rowHeight }} key={r}>
          {columns.map((col, c) => (
            <div
              className="rg-skeleton__cell"
              key={col.field}
              style={{ width: col.computedWidth }}
            >
              <Skeleton
                variant="text"
                width={CELL_WIDTHS[(r + c) % CELL_WIDTHS.length]}
                height={18}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
});

LoadingSkeletonOverlay.propTypes = {
  // Floor on the number of skeleton rows (default MIN_SKELETON_ROWS).
  minRows: PropTypes.number,
};

export default LoadingSkeletonOverlay;
